/** The person's main rail choices: which items are hidden and in what order.
 *
 *  Read and written through the Preferences store (navigation.hidden / navigation.order). The
 *  hidden list is also dual-written to the legacy user preferences key `main_rail_hidden` so
 *  clients that predate the store keep working, and that key seeds the store once when the store
 *  has nothing. The localStorage copy is a first-paint cache so the rail never flashes the full
 *  set before the store loads. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePreferences } from '../../features/preferences/PreferencesContext';
import { PINNED_NAVIGATION_IDS } from '../../shared-ui/navigation/navigationCustomisation';
import { API_BASE, getAuthHeaders } from '../../utils/supabase/client';

const PREFERENCE_KEY = 'main_rail_hidden';
/** The app's root destination: hiding it would leave the rail with no way home. */
export const MAIN_RAIL_PINNED_IDS: readonly string[] = PINNED_NAVIGATION_IDS;

export const mainRailCacheKey = (userId: string) => `quantis:main-rail-hidden:v1:${userId}`;

export function sanitiseHiddenIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.filter((v): v is string => typeof v === 'string' && !MAIN_RAIL_PINNED_IDS.includes(v))));
}

function readCache(userId: string): string[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(mainRailCacheKey(userId));
    return raw ? sanitiseHiddenIds(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

function writeCache(userId: string, ids: string[]): void {
  if (!userId) return;
  try {
    if (ids.length === 0) localStorage.removeItem(mainRailCacheKey(userId));
    else localStorage.setItem(mainRailCacheKey(userId), JSON.stringify(ids));
  } catch { /* the cache is an optimisation, never a requirement */ }
}

const endpoint = (userId: string) => `${API_BASE}/users/${userId}/preferences/${PREFERENCE_KEY}`;

// A later choice must never be overtaken by an earlier save or read.
const saveQueues = new Map<string, Promise<unknown>>();
const changeCounts = new Map<string, number>();
const seededUsers = new Set<string>();

async function saveToServer(userId: string, ids: string[]): Promise<void> {
  const res = await fetch(endpoint(userId), {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ value: ids }),
  });
  if (!res.ok && res.status !== 404) throw new Error(`Failed to save rail preference (${res.status})`);
}

async function readLegacyHidden(userId: string): Promise<string[] | null> {
  await (saveQueues.get(userId) ?? Promise.resolve());
  const res = await fetch(endpoint(userId), { headers: await getAuthHeaders() });
  if (!res.ok) return null;
  const data = await res.json();
  return Array.isArray(data?.value) ? sanitiseHiddenIds(data.value) : null;
}

export function useMainRailPreferences(userId: string) {
  const { preferences, loaded, update } = usePreferences();
  const stored = preferences.navigation;
  const [touched, setTouched] = useState(false);
  const legacyCache = useMemo(() => readCache(userId), [userId]);
  const storedHiddenRef = useRef(stored.hidden);
  storedHiddenRef.current = stored.hidden;

  // Until the store has spoken, the old device cache stops the rail flashing the full set.
  const hiddenIds = useMemo(
    () => sanitiseHiddenIds(loaded || touched || stored.hidden.length > 0 ? stored.hidden : legacyCache),
    [loaded, touched, stored.hidden, legacyCache],
  );

  // A person who set this before the store existed has it only in the old key: carry it over once.
  useEffect(() => {
    if (!userId || !loaded || seededUsers.has(userId)) return;
    seededUsers.add(userId);
    if (storedHiddenRef.current.length > 0) return;
    const startedAt = changeCounts.get(userId) ?? 0;
    void readLegacyHidden(userId)
      .then((ids) => {
        if (!ids || ids.length === 0) return;
        if ((changeCounts.get(userId) ?? 0) !== startedAt || storedHiddenRef.current.length > 0) return;
        update({ navigation: { hidden: ids } });
      })
      .catch(() => { /* offline or dead session: the store value stands */ });
  }, [userId, loaded, update]);

  const writeHidden = useCallback((next: string[]) => {
    const ids = sanitiseHiddenIds(next);
    changeCounts.set(userId, (changeCounts.get(userId) ?? 0) + 1);
    setTouched(true);
    update({ navigation: { hidden: ids } });
    writeCache(userId, ids);
    if (!userId) return;
    const queued = (saveQueues.get(userId) ?? Promise.resolve()).then(() => saveToServer(userId, ids));
    saveQueues.set(userId, queued.catch(() => {}));
    queued.catch(() => { /* kept in the store */ });
  }, [userId, update]);

  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);
  const setHidden = useCallback((id: string, hidden: boolean) => {
    const rest = hiddenIds.filter((h) => h !== id);
    writeHidden(hidden ? [...rest, id] : rest);
  }, [hiddenIds, writeHidden]);
  const setOrder = useCallback((order: string[]) => update({ navigation: { order } }), [update]);
  /** Back to the default rail: nothing hidden and the built-in order. */
  const reset = useCallback(() => {
    writeHidden([]);
    update({ navigation: { order: [] } });
  }, [writeHidden, update]);

  return { hiddenIds, hiddenSet, setHidden, reset, order: stored.order, setOrder };
}
